import { CSSInterpolation } from "@mui/material";
import { Theme } from "@mui/material/styles";
import type { ConsoleMode } from "../theme";
import {
  contentMaxWidth,
  elevation,
  focus,
  headerHeight,
  heroLine,
  heroPanel,
  overlay,
  print,
  radius,
  ruleHeight,
  space,
  touchTarget,
} from "./tokens";
import { fontFamily, fontMono } from "./typography";

const px = (n: number) => `${n}px`;
const ms = (n: number) => `${n}ms`;

/**
 * CSS custom properties for non-MUI elements: `--pft-<colorToken>` for every color token of the
 * active mode, plus `--pft-<group>-<name>` for the non-color tokens. Motion values follow the
 * theme, so they read 0ms under reduced motion.
 */
export const cssVariables = (
  mode: ConsoleMode,
  theme: Theme,
): Record<string, string> => {
  const vars: Record<string, string> = {};
  for (const [name, value] of Object.entries(theme.palette.tokens)) {
    vars[`--pft-${name}`] = value;
  }
  vars["--pft-overlay"] = overlay[mode];
  vars["--pft-heroPanel"] = heroPanel[mode];
  vars["--pft-heroLine"] = heroLine[mode];
  vars["--pft-space"] = px(space);
  vars["--pft-contentMaxWidth"] = px(contentMaxWidth);
  vars["--pft-headerHeight"] = px(headerHeight);
  vars["--pft-ruleHeight"] = px(ruleHeight);
  for (const [name, value] of Object.entries(radius)) {
    vars[`--pft-radius-${name}`] = px(value);
  }
  vars["--pft-elevation-card"] = elevation.card[mode];
  vars["--pft-elevation-menu"] = elevation.menu;
  vars["--pft-glow-presidential"] = elevation.glowPresidential[mode];
  vars["--pft-glow-national"] = elevation.glowNational[mode];
  const { duration } = theme.transitions;
  vars["--pft-motion-fast"] = ms(duration.short);
  vars["--pft-motion-base"] = ms(duration.standard);
  vars["--pft-motion-slow"] = ms(duration.complex);
  vars["--pft-focus-width"] = px(focus.width);
  vars["--pft-focus-offset"] = px(focus.offset);
  vars["--pft-touchTarget"] = px(touchTarget);
  vars["--pft-font"] = fontFamily;
  vars["--pft-font-mono"] = fontMono;
  return vars;
};

/** `MuiCssBaseline.styleOverrides`: tokens as CSS variables, focus ring, reduced motion, print. */
export const globalStyles =
  (mode: ConsoleMode) =>
  (theme: Theme): CSSInterpolation => ({
    ":root": cssVariables(mode, theme),
    body: {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      fontFamily,
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
    },
    a: { color: theme.palette.primaryInk },
    "a:hover": { color: theme.palette.dangerInk },
    "code, kbd, samp": { fontFamily: fontMono },
    "*:focus-visible": {
      outline: `${px(focus.width)} solid var(--pft-focusRing)`,
      outlineOffset: px(focus.offset),
    },
    ":focus:not(:focus-visible)": { outline: "none" },
    "@media (prefers-reduced-motion: reduce)": {
      "*, *::before, *::after": {
        transitionDuration: "0.01ms !important",
        animationDuration: "0.01ms !important",
        animationIterationCount: "1 !important",
        scrollBehavior: "auto !important",
      },
      html: { scrollBehavior: "auto" },
    },
    "@media print": {
      "[data-print-hide]": { display: "none !important" },
      "html, body": {
        background: `${print.paper} !important`,
        color: `${print.ink} !important`,
      },
      "*, *::before, *::after": { boxShadow: "none !important" },
      ".MuiPaper-root": {
        background: `${print.paper} !important`,
        color: `${print.ink} !important`,
      },
      // Dark-palette text (text.secondary, primaryInk, tier inks) is light and would print
      // light-on-white, so in dark mode every element takes the print ink from body/Paper.
      ...(mode === "dark" ? { "body *": { color: "inherit !important" } } : {}),
      "table, .MuiTableContainer-root": {
        overflow: "visible !important",
        maxHeight: "none !important",
        width: "100% !important",
      },
    },
  });
